/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  Avatar,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BadgeIcon from "@mui/icons-material/Badge";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { convertUTCtoDubai } from "utils/Constant";
import axios from "axios";
import { useSnackbar } from "notistack";
import { getDecodedUserData } from "utils/Constant";

const ProfileModal = ({ open, onClose }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { enqueueSnackbar } = useSnackbar();
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchUserData();
    }
  }, [open]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const userID = getDecodedUserData()?.userID;
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}user-service-and-maintenance/user/${userID}`,
        {
          withCredentials: true
        }
      );

      if (response.data.success === true) {
        setUserData(response.data.data);
      }
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error.message, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };


  const InfoItem = ({ icon, label, value }) => (
    <MDBox
      display="flex"
      alignItems="center"
      gap={2}
      p={2}
      sx={{
        borderRadius: "12px",
        backgroundColor: darkMode
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.02)",
        transition: "all 0.3s ease",
        "&:hover": {
          backgroundColor: darkMode
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.04)",
          transform: "translateY(-2px)",
          boxShadow: darkMode
            ? "0 4px 12px rgba(0, 0, 0, 0.3)"
            : "0 4px 12px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <MDBox
        sx={{
          width: 48,
          height: 48,
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(195deg, #EF5350, #E53935)`,
          boxShadow: darkMode
            ? "0 4px 20px 0 rgba(0, 0, 0, 0.3)"
            : "0 4px 20px 0 rgba(0, 0, 0, 0.14)",
        }}
      >
        {icon}
      </MDBox>
      <MDBox flex={1}>
        <MDTypography
          variant="caption"
          fontWeight="regular"
          color="text"
          sx={{ opacity: 0.7, textTransform: "uppercase", fontSize: "0.75rem" }}
        >
          {label}
        </MDTypography>
        <MDTypography variant="h6" fontWeight="medium" sx={{ mt: 0.5 }}>
          {value || "N/A"}
        </MDTypography>
      </MDBox>
    </MDBox>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: darkMode ? "#1a2035" : "#fff",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          pt: 3,
          px: 3,
        }}
      >
        <MDBox display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              background: `linear-gradient(195deg, #EF5350, #E53935)`,
              boxShadow: darkMode
                ? "0 4px 20px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 20px 0 rgba(0, 0, 0, 0.14)",
            }}
          >
            <PersonIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Profile Information
            </MDTypography>
            <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
              View your account details
            </MDTypography>
          </MDBox>
        </MDBox>
        <IconButton
          onClick={onClose}
          sx={{
            color: darkMode ? "#fff" : "#000",
            "&:hover": {
              backgroundColor: darkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ mx: 3 }} />

      <DialogContent sx={{ px: 3, py: 3 }}>
        {loading ? (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
          >
            <MDTypography variant="h6">Loading...</MDTypography>
          </MDBox>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InfoItem
                icon={<PersonIcon sx={{ color: "#fff", fontSize: 24 }} />}
                label="Username"
                value={userData?.username}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoItem
                icon={<BadgeIcon sx={{ color: "#fff", fontSize: 24 }} />}
                label="Department"
                value={userData?.department}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoItem
                icon={<PhoneIcon sx={{ color: "#fff", fontSize: 24 }} />}
                label="Phone Number"
                value={userData?.phone_number}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoItem
                icon={<BusinessIcon sx={{ color: "#fff", fontSize: 24 }} />}
                label="Company"
                value={userData?.company}
              />
            </Grid>
            <Grid item xs={12}>
              <InfoItem
                icon={<EmailIcon sx={{ color: "#fff", fontSize: 24 }} />}
                label="Email ID"
                value={userData?.email}
              />
            </Grid>
            <Grid item xs={12}>
              <InfoItem
                icon={
                  <CalendarTodayIcon sx={{ color: "#fff", fontSize: 24 }} />
                }
                label="Account Created"
                value={convertUTCtoDubai(userData?.createdAt)}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;
